import { useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  GARMENT_CATEGORIES,
  OCCASIONS,
  SEASONS,
  type Occasion,
  type Season,
  type WardrobeItemInput,
} from '../../shared/wardrobe';
import type { ProductImportPreview } from '../../shared/productImport';
import { Icon } from './Icon';
import { startGarmentImageJob } from '../lib/imageProcessing';
import {
  confirmProductImport,
  importedImageUrl,
  previewProductImport,
} from '../lib/productImport';
import { startStudioPreview, waitForStudioPreview } from '../lib/studioPreview';

const MAX_LINKS = 5;

type CleanImportPreview =
  | { status: 'processing' }
  | { status: 'completed'; url: string; id: string }
  | { status: 'failed' };

function cleanPreviewKey(previewId: string, imageUrl: string): string {
  return `${previewId}|${imageUrl}`;
}

interface EditableImport {
  preview: ProductImportPreview;
  imageUrl: string;
  item: WardrobeItemInput;
  selectedSize: string;
  selectedColor: string;
  price: string;
  currency: string;
  requestKey: string;
  status: 'ready' | 'saving' | 'saved';
  savedItemId: string | null;
  error: string;
}

interface FailedImport {
  url: string;
  message: string;
}

function currencyDigits(currency: string): number {
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: currency || 'INR',
    }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

function priceFromMinor(value: number | null, currency: string | null): string {
  if (value === null) return '';
  return (value / 10 ** currencyDigits(currency ?? 'INR')).toFixed(
    currencyDigits(currency ?? 'INR'),
  );
}

function priceToMinor(value: string, currency: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 10 ** currencyDigits(currency));
}

function editableImport(preview: ProductImportPreview): EditableImport {
  const candidate = preview.candidate;
  return {
    preview,
    imageUrl: candidate.images[0]?.url ?? '',
    item: {
      name: candidate.name,
      category: candidate.category,
      primaryColor: candidate.primaryColor,
      brand: candidate.brand,
      material: candidate.material,
      notes: candidate.notes,
      seasons: candidate.seasons,
      occasions: candidate.occasions,
      favorite: false,
    },
    selectedSize: candidate.selectedSize,
    selectedColor: candidate.selectedColor,
    price: priceFromMinor(candidate.listedPriceMinor, candidate.currency),
    currency: candidate.currency ?? 'INR',
    requestKey: crypto.randomUUID(),
    status: 'ready',
    savedItemId: null,
    error: '',
  };
}

function toggle<T extends string>(value: T, values: T[]): T[] {
  return values.includes(value) ? values.filter((entry) => entry !== value) : [...values, value];
}

export function ShoppingLinkImporter({ onUsePhoto }: { onUsePhoto: () => void }) {
  const [links, setLinks] = useState('');
  const [imports, setImports] = useState<EditableImport[]>([]);
  const [failures, setFailures] = useState<FailedImport[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [formError, setFormError] = useState('');
  const [cleanPreviews, setCleanPreviews] = useState<Record<string, CleanImportPreview>>({});
  const startedCleanPreviews = useRef(new Set<string>());

  async function prepareCleanImportPreview(
    previewId: string,
    imageUrl: string,
    imageIndex: number,
    item: WardrobeItemInput,
  ) {
    const key = cleanPreviewKey(previewId, imageUrl);
    if (!imageUrl || startedCleanPreviews.current.has(key)) return;
    startedCleanPreviews.current.add(key);
    setCleanPreviews((current) => ({ ...current, [key]: { status: 'processing' } }));
    try {
      const source = await fetch(importedImageUrl(previewId, imageUrl), {
        credentials: 'include',
      });
      if (!source.ok) throw new Error('The product image could not be loaded.');
      const started = await startStudioPreview(
        await source.blob(),
        `${previewId}-img-${imageIndex}`,
        { name: item.name, category: item.category, primaryColor: item.primaryColor },
      );
      const finished =
        started.status === 'processing' ? await waitForStudioPreview(started.id) : started;
      setCleanPreviews((current) => ({
        ...current,
        [key]:
          finished.status === 'completed' && finished.previewUrl
            ? { status: 'completed', url: finished.previewUrl, id: finished.id }
            : { status: 'failed' },
      }));
    } catch {
      setCleanPreviews((current) => ({ ...current, [key]: { status: 'failed' } }));
    }
  }

  function updateImport(index: number, update: (entry: EditableImport) => EditableImport) {
    setImports((current) =>
      current.map((entry, entryIndex) => (entryIndex === index ? update(entry) : entry)),
    );
  }

  async function analyzeLinks() {
    const urls = [
      ...new Set(
        links
          .split(/\s+/)
          .map((entry) => entry.trim())
          .filter(Boolean),
      ),
    ];
    setFormError('');
    setFailures([]);
    setImports([]);
    if (!urls.length) {
      setFormError('Paste at least one public product link.');
      return;
    }
    if (urls.length > MAX_LINKS) {
      setFormError(`Import up to ${MAX_LINKS} links at a time.`);
      return;
    }

    setAnalyzing(true);
    const ready: EditableImport[] = [];
    const failed: FailedImport[] = [];
    for (const url of urls) {
      try {
        ready.push(editableImport(await previewProductImport(url)));
      } catch (caught) {
        failed.push({
          url,
          message: caught instanceof Error ? caught.message : 'This product could not be analyzed.',
        });
      }
    }
    setImports(ready);
    setFailures(failed);
    setAnalyzing(false);
    for (const entry of ready) {
      void prepareCleanImportPreview(entry.preview.previewId, entry.imageUrl, 0, entry.item);
    }
  }

  async function saveImport(index: number) {
    const entry = imports[index];
    if (!entry || entry.status !== 'ready') return;
    updateImport(index, (current) => ({ ...current, status: 'saving', error: '' }));
    try {
      const cleanPreview = cleanPreviews[cleanPreviewKey(entry.preview.previewId, entry.imageUrl)];
      const studioPreviewId = cleanPreview?.status === 'completed' ? cleanPreview.id : null;
      const saved = await confirmProductImport(
        {
          previewId: entry.preview.previewId,
          imageUrl: entry.imageUrl,
          studioPreviewId,
          item: entry.item,
          selectedSize: entry.selectedSize,
          selectedColor: entry.selectedColor,
          listedPriceMinor: priceToMinor(entry.price, entry.currency),
          currency: entry.currency.trim().toUpperCase() || null,
        },
        entry.requestKey,
      );
      if (!studioPreviewId) {
        // The reviewed clean image was unavailable, so the retailer photo was
        // stored; the after-save cleanup remains the fallback.
        await startGarmentImageJob(saved.id, `${entry.requestKey}-studio`, 'studio-photo', {
          autoApply: true,
        }).catch(() => undefined);
      }
      updateImport(index, (current) => ({
        ...current,
        status: 'saved',
        savedItemId: saved.id,
      }));
    } catch (caught) {
      updateImport(index, (current) => ({
        ...current,
        status: 'ready',
        error: caught instanceof Error ? caught.message : 'This item could not be saved.',
      }));
    }
  }

  const savedCount = imports.filter((entry) => entry.status === 'saved').length;

  return (
    <div className="shopping-import-shell">
      <section className="shopping-link-card">
        <span className="card-icon card-icon--rose">
          <Icon name="sparkles" size={22} />
        </span>
        <div>
          <p className="eyebrow">AI shopping import</p>
          <h2>Paste the product links</h2>
          <p>
            Add up to five links from different stores. AI reads each visible product page and
            prepares an editable closet card.
          </p>
        </div>
        <label className="field field--wide">
          <span>Shopping links</span>
          <textarea
            aria-describedby="shopping-link-help"
            placeholder={'https://store.example/product/your-item\nhttps://another-store.example/item'}
            rows={5}
            value={links}
            onChange={(event) => setLinks(event.target.value)}
          />
        </label>
        <p className="shopping-link-help" id="shopping-link-help">
          One link per line. Public HTTPS product pages work best; login, cart, and tracking links
          are not imported.
        </p>
        {formError ? (
          <p className="form-error" role="alert">
            {formError}
          </p>
        ) : null}
        <div className="form-actions">
          <button className="button button--secondary" type="button" onClick={onUsePhoto}>
            Use a photo instead
          </button>
          <button
            className="button button--primary"
            disabled={analyzing}
            type="button"
            onClick={() => void analyzeLinks()}
          >
            {analyzing ? 'Reading product pages…' : 'Find my items'}
            {!analyzing ? <Icon name="arrow" size={18} /> : null}
          </button>
        </div>
      </section>

      {failures.map((failure) => (
        <section className="import-failure-card" key={failure.url}>
          <span className="card-icon card-icon--sage">
            <Icon name="search" size={20} />
          </span>
          <div>
            <strong>Could not import this link</strong>
            <small>{failure.url}</small>
            <p>{failure.message}</p>
          </div>
          <button className="button button--secondary" type="button" onClick={onUsePhoto}>
            Upload photo
          </button>
        </section>
      ))}

      {imports.map((entry, index) => {
        const candidate = entry.preview.candidate;
        const lowConfidence = candidate.confidence.overall < 0.72;
        return (
          <section className="import-entry-card" key={entry.preview.previewId}>
            <div className="import-entry-heading">
              <div>
                <p className="eyebrow">{candidate.store}</p>
                <h2>{entry.item.name || 'Review imported item'}</h2>
              </div>
              <span className={lowConfidence ? 'confidence-badge is-low' : 'confidence-badge'}>
                {Math.round(candidate.confidence.overall * 100)}% AI confidence
              </span>
            </div>

            {entry.preview.duplicateItemId ? (
              <p className="import-warning">
                This shopping link may already be in your closet.{' '}
                <NavLink to={`/closet/${entry.preview.duplicateItemId}`}>View existing item</NavLink>
              </p>
            ) : null}
            {lowConfidence ? (
              <p className="import-warning">
                AI was not certain about every detail. Check the highlighted information before
                saving.
              </p>
            ) : null}

            <div className="import-entry-layout">
              <div className="import-photo-review">
                {entry.imageUrl ? (
                  (() => {
                    const clean =
                      cleanPreviews[cleanPreviewKey(entry.preview.previewId, entry.imageUrl)];
                    if (clean?.status === 'completed') {
                      return (
                        <img alt={entry.item.name || 'Clean garment image'} src={clean.url} />
                      );
                    }
                    if (clean?.status === 'failed') {
                      return (
                        <>
                          <img
                            alt={entry.item.name || 'Imported product'}
                            src={importedImageUrl(entry.preview.previewId, entry.imageUrl)}
                          />
                          <p aria-live="polite" className="clean-preview-status" role="status">
                            The clean image could not be prepared; this piece will be cleaned
                            after saving.
                          </p>
                        </>
                      );
                    }
                    return (
                      <div aria-live="polite" className="clean-preview-loading" role="status">
                        <span className="clean-preview-loading__pulse" />
                        Creating the clean garment-only image…
                      </div>
                    );
                  })()
                ) : (
                  <div className="import-photo-empty">Choose a product image</div>
                )}
                <a
                  className="import-source-link"
                  href={candidate.sourceUrl}
                  rel="noreferrer"
                  target="_blank"
                >
                  View original product
                </a>
              </div>

              <div className="import-review-form">
                <div className="field-grid">
                  <label className="field field--wide">
                    <span>Product name</span>
                    <input
                      required
                      value={entry.item.name}
                      onChange={(event) =>
                        updateImport(index, (current) => ({
                          ...current,
                          item: { ...current.item, name: event.target.value },
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Category</span>
                    <select
                      value={entry.item.category}
                      onChange={(event) =>
                        updateImport(index, (current) => ({
                          ...current,
                          item: {
                            ...current.item,
                            category: event.target.value as WardrobeItemInput['category'],
                          },
                        }))
                      }
                    >
                      {GARMENT_CATEGORIES.map((option) => (
                        <option key={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>Main colour</span>
                    <input
                      required
                      value={entry.item.primaryColor}
                      onChange={(event) =>
                        updateImport(index, (current) => ({
                          ...current,
                          item: { ...current.item, primaryColor: event.target.value },
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Brand</span>
                    <input
                      value={entry.item.brand ?? ''}
                      onChange={(event) =>
                        updateImport(index, (current) => ({
                          ...current,
                          item: { ...current.item, brand: event.target.value },
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Material</span>
                    <input
                      value={entry.item.material ?? ''}
                      onChange={(event) =>
                        updateImport(index, (current) => ({
                          ...current,
                          item: { ...current.item, material: event.target.value },
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Purchased size</span>
                    <input
                      value={entry.selectedSize}
                      onChange={(event) =>
                        updateImport(index, (current) => ({
                          ...current,
                          selectedSize: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Selected colour</span>
                    <input
                      value={entry.selectedColor}
                      onChange={(event) =>
                        updateImport(index, (current) => ({
                          ...current,
                          selectedColor: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Listed price</span>
                    <input
                      inputMode="decimal"
                      min="0"
                      step="0.01"
                      type="number"
                      value={entry.price}
                      onChange={(event) =>
                        updateImport(index, (current) => ({
                          ...current,
                          price: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Currency</span>
                    <input
                      maxLength={3}
                      value={entry.currency}
                      onChange={(event) =>
                        updateImport(index, (current) => ({
                          ...current,
                          currency: event.target.value.toUpperCase(),
                        }))
                      }
                    />
                  </label>
                  <label className="field field--wide">
                    <span>Notes</span>
                    <textarea
                      rows={4}
                      value={entry.item.notes ?? ''}
                      onChange={(event) =>
                        updateImport(index, (current) => ({
                          ...current,
                          item: { ...current.item, notes: event.target.value },
                        }))
                      }
                    />
                  </label>
                </div>

                <fieldset className="choice-fieldset">
                  <legend>Season</legend>
                  <div className="choice-chips">
                    {SEASONS.map((option) => (
                      <button
                        aria-pressed={entry.item.seasons.includes(option)}
                        className={entry.item.seasons.includes(option) ? 'is-selected' : ''}
                        key={option}
                        type="button"
                        onClick={() =>
                          updateImport(index, (current) => ({
                            ...current,
                            item: {
                              ...current.item,
                              seasons: toggle<Season>(option, current.item.seasons),
                            },
                          }))
                        }
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="choice-fieldset">
                  <legend>Occasion</legend>
                  <div className="choice-chips">
                    {OCCASIONS.map((option) => (
                      <button
                        aria-pressed={entry.item.occasions.includes(option)}
                        className={entry.item.occasions.includes(option) ? 'is-selected' : ''}
                        key={option}
                        type="button"
                        onClick={() =>
                          updateImport(index, (current) => ({
                            ...current,
                            item: {
                              ...current.item,
                              occasions: toggle<Occasion>(option, current.item.occasions),
                            },
                          }))
                        }
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <label className="favorite-check">
                  <input
                    checked={Boolean(entry.item.favorite)}
                    type="checkbox"
                    onChange={(event) =>
                      updateImport(index, (current) => ({
                        ...current,
                        item: { ...current.item, favorite: event.target.checked },
                      }))
                    }
                  />
                  <span className="card-icon card-icon--rose">
                    <Icon name="heart" size={20} />
                  </span>
                  <span>
                    <strong>Favourite piece</strong>
                    <small>Keep it close when building outfits.</small>
                  </span>
                </label>

                {entry.error ? (
                  <p className="form-error" role="alert">
                    {entry.error}
                  </p>
                ) : null}
                <div className="form-actions">
                  {entry.status === 'saved' && entry.savedItemId ? (
                    <NavLink className="button button--secondary" to={`/closet/${entry.savedItemId}`}>
                      View item
                    </NavLink>
                  ) : null}
                  <button
                    className="button button--primary"
                    disabled={
                      entry.status !== 'ready' ||
                      !entry.imageUrl ||
                      !entry.item.name.trim() ||
                      !entry.item.primaryColor.trim()
                    }
                    type="button"
                    onClick={() => void saveImport(index)}
                  >
                    {entry.status === 'saving'
                      ? 'Saving privately…'
                      : entry.status === 'saved'
                        ? 'Added to closet'
                        : 'Add to my closet'}
                    {entry.status === 'ready' ? <Icon name="arrow" size={18} /> : null}
                  </button>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {savedCount ? (
        <div className="import-complete-bar" role="status">
          <span>
            <Icon name="check" size={18} />
            {savedCount} {savedCount === 1 ? 'item' : 'items'} added privately
          </span>
          <NavLink className="button button--primary" to="/closet">
            Open closet
          </NavLink>
        </div>
      ) : null}
    </div>
  );
}
